import { useEffect, useImperativeHandle, useState } from 'react'
import apiGet from 'lib/network/apiGet'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import useParams from 'hooks/useGetParams'
import get from 'lodash/get'
import some from 'lodash/some'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import keys from 'lodash/keys'
import uniqBy from 'lodash/uniqBy'
import uniqWith from 'lodash/uniqWith'
import queryString from 'query-string'
import PageFooter from 'components/paginationComponent'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { isCompanyDashboardUser, isPartnerDashboardUser } from 'lib/auth/dashboardRole'

const WithReports = ({
  isReg,
  isCampaign,
  isTransaction,
  isExternalUser,
  onRowClick,
  workspace,
  setSearchKey,
  forwardedRef,
  apiDownloadURL,
  q,
  currType,
  noTable,
  columns,
  apiURL,
  status,
  dateRange,
  search,
  sStatus,
  setDateRange,
  setScanStatus,
  apiQueryString,
  bgRow,
  streamConnected,
  suppressStreamBanner = false,
  onDataUpdate,
  bookingCreatedBy,
  paymentMethod,
  bookingStatus,
  paymentStatus,
  partner,
  bookDateTime,
  bookStartDateTime,
  bookEndDateTime,
  companyIds,
  companyGovernorates,
  companyMinAge,
  companyMaxAge,
  blockedCountries,
  instantBooking,
  ajarleePercentageMin,
  ajarleePercentageMax,
  companyDepositMin,
  companyDepositMax,
  vatTax,
  delivery,
  deliveryFeeMin,
  deliveryFeeMax,

  viewType = 'table',
  CardComponent,
}) => {
  const [filter, setFilter] = useParams({ workspace })
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [users, setUsers] = useState([])
  const { t } = useTranslation()
  const companyBranchFilterId = useSelector(state => get(state, 'settings.companyBranchFilterId'))
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const isCompanyRole = isCompanyDashboardUser(userFromStore)
  const isPartnerRole = isPartnerDashboardUser(userFromStore)
  const hasAdminAccess = some(userFromStore?.roles, { type: 'admin' })
  const companyReportPadding = (isCompanyRole || isPartnerRole) && !hasAdminAccess
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  useEffect(() => {
    const qs = queryString.parse(window.location.search)
    if (!qs.ed && !qs.sd) {
      setDateRange({})
    }
    if (!qs.search) {
      setSearchKey('')
    }
    if (!qs.scanStatus) {
      setScanStatus(null)
    }
  }, [])

  useEffect(() => {
    if (dateRange && (dateRange.sd !== filter.sd || dateRange.ed !== filter.ed)) {
      setFilter(prev => ({ ...prev, ...dateRange }))
      setPagination({ currentPage: 0 })
      setUsers([])
    }
  }, [dateRange])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      bookDateTime: bookDateTime || null,
      bookStartDateTime: bookStartDateTime || null,
      bookEndDateTime: bookEndDateTime || null,
    }))
    setPagination({ currentPage: 0 })
    setUsers([])
  }, [bookDateTime, bookStartDateTime, bookEndDateTime])

  useEffect(() => {
    // Bookings and other screens omit `workspace`; only wait when parent passed a workspace id
    // but URL state has not synced yet (filter.workspace still null).
    const waitingForWorkspaceSelection = workspace != null && filter.workspace == null
    if (waitingForWorkspaceSelection) return
    fetchData(false, { resetPage: true })
  }, [filter, companyBranchFilterId, workspace])

  useEffect(() => {
    if (sStatus) {
      setFilter({ ...filter, scanStatus: sStatus })
      setPagination({ currentPage: 0 })
      setUsers([])
    }
  }, [sStatus])

  useEffect(() => {
    searchList(search)
  }, [search])

  // Only mirror URL -> input when the URL changes on its own (back/forward); reacting to `search`
  // too would overwrite what the user is typing with the not-yet-updated URL value.
  useEffect(() => {
    const activeSearch = search?.length >= 2 ? search : null
    if ((filter.search ?? null) !== activeSearch && setSearchKey) {
      setSearchKey(filter.search || '')
    }
  }, [filter.search])

  useEffect(() => {
    if (workspace != null) {
      setFilter({ ...filter, workspace })
      setPagination({ currentPage: 0 })
      setUsers([])
    }
  }, [workspace])
  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      bookingCreatedBy,
    }))

    setPagination({ currentPage: 0 })
    setUsers([])
  }, [bookingCreatedBy])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      bookingStatus,
    }))
    setPagination({ currentPage: 0 })
    setUsers([])
  }, [bookingStatus])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      paymentStatus,
    }))
    setPagination({ currentPage: 0 })
    setUsers([])
  }, [paymentStatus])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      partner,
    }))
    setPagination({ currentPage: 0 })
    setUsers([])
  }, [partner])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      paymentMethod,
    }))

    setPagination({ currentPage: 0 })
    setUsers([])
  }, [paymentMethod])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      companyIds: companyIds?.length ? companyIds : null,
      companyGovernorates: companyGovernorates?.length ? companyGovernorates : null,
      companyMinAge: companyMinAge || null,
      companyMaxAge: companyMaxAge || null,
      blockedCountries: blockedCountries?.length ? blockedCountries : null,
      instantBooking: instantBooking || null,
      ajarleePercentageMin: ajarleePercentageMin || null,
      ajarleePercentageMax: ajarleePercentageMax || null,
      companyDepositMin: companyDepositMin || null,
      companyDepositMax: companyDepositMax || null,
      vatTax: vatTax || null,
      delivery: delivery || null,
      deliveryFeeMin: deliveryFeeMin || null,
      deliveryFeeMax: deliveryFeeMax || null,
    }))
    setPagination({ currentPage: 0 })
    setUsers([])
  }, [
    companyIds,
    companyGovernorates,
    companyMinAge,
    companyMaxAge,
    blockedCountries,
    instantBooking,
    ajarleePercentageMin,
    ajarleePercentageMax,
    companyDepositMin,
    companyDepositMax,
    vatTax,
    delivery,
    deliveryFeeMin,
    deliveryFeeMax,
  ])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      status,
    }))
    setPagination({ currentPage: 0 })
    setUsers([])
  }, [status])

  useEffect(() => {
    if (q) {
      if (keys(q)?.length > 0) {
        setFilter({ ...filter, q: JSON.stringify(q) })
        setPagination({ currentPage: 0 })
        setUsers([])
      } else {
        setFilter(prev => ({
          ...prev,
          q: '',
        }))
      }
    }
  }, [q])

  useEffect(() => {
    if (onDataUpdate && users.length > 0) {
      onDataUpdate(users)
    }
  }, [users, onDataUpdate])

  useImperativeHandle(forwardedRef, () => ({
    download: async (setDownloading, cb) => {
      setDownloading(true)
      const response = await apiGet(apiDownloadURL, { size: 9999999, page: 0, ...filter, ...apiQueryString })
      if (response.data?.content) {
        const csvContent = 'data:text/csv;charset=utf-8,' + response?.data?.content

        const encodedUri = encodeURI(csvContent)
        const link = document.createElement('a')
        link.setAttribute('href', encodedUri)
        link.setAttribute('download', response?.data?.name)
        document.body.appendChild(link)

        link.click() // This will download the data file named "my_data.csv".
      } else {
        window.open(`${process.env.REACT_APP_BASE_URL}/admin/registrations/download/${response?.data?.name}`, '_blank')
      }

      setDownloading(false)

      cb()
    },
    removeBooking: bookingId => {
      if (!bookingId) return
      setUsers(prevUsers => prevUsers.filter(existingBooking => (existingBooking._id || existingBooking.id) !== bookingId))
    },
    addNewBooking: booking => {
      if (booking) {
        const bookingId = booking._id || booking.id

        // eslint-disable-next-line
        const exists = users.some(existingBooking => {
          const existingId = existingBooking._id || existingBooking.id
          return existingId === bookingId
        })

        if (!exists) {
          setUsers(prevUsers => [booking, ...prevUsers])
        }
      }
    },
  }))

  const fetchData = async (noLoading = false, options = {}) => {
    const { resetPage } = options
    if (!noLoading) setLoading(true)
    setIsLoadingMore(true)
    const pageForApi = resetPage ? 1 : (pagination?.currentPage || 0) + 1
    const response = await apiGet(apiURL, { size: 30, page: pageForApi, ...filter, ...apiQueryString })

    setPagination(response?.data?.pagination)
    if (isReg) {
      const data = response.data?.list
      setUsers(prev => (resetPage ? uniqBy(data, 'id') : uniqBy([...prev, ...data], 'id')))
    } else if (isCampaign) {
      const data = response.data?.list
      setUsers(prev => (resetPage ? uniqWith(data, isEqual) : uniqWith([...prev, ...data], isEqual)))
    } else {
      const data = response.data?.list
      setUsers(prev => (resetPage ? data : [...prev, ...data]))
    }

    setIsLoadingMore(false)

    setLoading(false)
  }

  const searchList = async value => {
    setPagination({ currentPage: 0 })
    setUsers([])
    setFilter(prev => ({
      ...prev,
      search: value?.length >= 2 ? value : null,
    }))
  }

  return (
    <div className={classNames('flex flex-col', companyReportPadding && 'pb-12 lg:pb-24 2xl:pb-0')}>
      <div className="flex-1 flex items-stretch">
        <main className="flex-1">
          <div className="align-middle inline-block min-w-full">
            {(isReg || isCampaign) && (
              <div className="px-4 py-2 absolute bottom-4 left-[50%]">
                {isReg && users?.length !== pagination?.count && (
                  <span className="tag">
                    Showing {users?.length} of {pagination?.count}
                  </span>
                )}
                {isCampaign && <span className="tag">Campaign data queried from {users?.reduce((a, b) => a + parseInt(b?.count), 0)} users</span>}
                {isReg &&
                  keys(q).map((item, index) =>
                    !isEmpty(q[item]) ? (
                      <span key={index} className="tag">
                        {item} : {q[item]}
                      </span>
                    ) : null
                  )}
              </div>
            )}
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />

            <div className={classNames('min-w-full', companyReportPadding && 'px-4 pt-1 sm:px-5 sm:pt-3')}>
              {!loading ? (
                <>
                  {streamConnected && !suppressStreamBanner ? (
                    <div className="flex justify-start mt-4">
                      <div className="mb-4 text-xs text-green-600 p-2 bg-green-50 rounded-md">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {t('booking.messages.liveUpdatesConnected')}
                      </div>
                    </div>
                  ) : null}

                  {users?.length === 0 ? (
                    <EmptyState v2 description={t('common.noData')} />
                  ) : viewType === 'card' && CardComponent ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full pb-8 lg:pb-4">
                      {users.map((item, index) => (
                        <div key={item._id || item.id || index} className="min-w-0">
                          <CardComponent item={item} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Table
                      bgRow={bgRow}
                      sortBy={filter.sortBy}
                      sortOrder={filter.sortOrder}
                      handleSort={(sortBy, sortOrder) => {
                        setUsers([])
                        setFilter({ ...filter, sortBy, sortOrder })
                        if (isReg || isTransaction) {
                          setPagination({ currentPage: 0 })
                        }
                      }}
                      // onRowClick={isReg ? onRowClick : null}
                      noTable={noTable}
                      hasMore={pagination?.currentPage !== pagination?.totalPages}
                      fetchData={() => fetchData(true)}
                      data={isReg ? uniqBy(users, 'id') : users}
                      columns={columns}
                    />
                  )}

                  {/* Load More Button - Only for card view */}
                  {/* Load More Button - Only for card view */}
                  {viewType === 'card' && users?.length > 0 && pagination?.currentPage !== pagination?.totalPages && (
                    <div className="flex justify-center mt-6 mb-12 lg:mb-16 2xl:mb-4 relative z-[1200]">
                      <button
                        onClick={() => fetchData(true)}
                        className="px-6 py-2 bg-dark-600 mb-10 text-white rounded-md hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        disabled={loading || isLoadingMore}
                      >
                        {/* {loading ? 'Loading...' : 'Load More'} */}
                        {loading || isLoadingMore ? t('common.loading') : t('common.loadMore')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex min-h-[200px] w-full flex-col items-center justify-center border-0 py-8">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default WithReports
